import React, { useEffect, useState } from "react";
import {
  getAssetSourceStats,
  getRecentAssetEntries,
  isCmsDebugEnabled,
  subscribeCmsSourceUpdates,
  type ContentLoadMeta,
} from "./contentSource";
import { getCurrentCmsEnvironment, getGcsBaseUrl, shouldUseBundledCmsAssets } from "./gcsConfig";

interface ContentSourceIndicatorProps {
  meta: ContentLoadMeta;
}

const MINIMIZED_KEY = "genius-cms-debug-minimized";

const sourceColor: Record<string, string> = {
  gcs: "#22c55e",
  local: "#f59e0b",
  project: "#94a3b8",
};

export const ContentSourceIndicator: React.FC<ContentSourceIndicatorProps> = ({ meta }) => {
  const [, tick] = useState(0);
  const [minimized, setMinimized] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(MINIMIZED_KEY) === "1";
  });

  useEffect(() => {
    return subscribeCmsSourceUpdates(() => tick((n) => n + 1));
  }, []);

  const toggleMinimized = () => {
    setMinimized((prev) => {
      const next = !prev;
      window.localStorage.setItem(MINIMIZED_KEY, next ? "1" : "0");
      return next;
    });
  };

  if (!isCmsDebugEnabled()) return null;

  const stats = getAssetSourceStats();
  const recent = getRecentAssetEntries();
  const gcsBase = getGcsBaseUrl();
  const jsonLabel = meta.jsonSource === "gcs" ? "GCS" : "Local JSON";
  const jsonColor = meta.jsonSource === "gcs" ? "#22c55e" : "#f59e0b";

  const shellStyle: React.CSSProperties = {
    position: "fixed",
    bottom: 12,
    left: 12,
    zIndex: 99999,
    borderRadius: 8,
    background: "rgba(15, 23, 42, 0.94)",
    color: "#f8fafc",
    fontSize: 11,
    lineHeight: 1.45,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  };

  const toggleButtonStyle: React.CSSProperties = {
    border: "none",
    background: "rgba(255,255,255,0.12)",
    color: "#f8fafc",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
  };

  if (minimized) {
    return (
      <button
        type="button"
        onClick={toggleMinimized}
        aria-label="Expand Genius CMS debug panel"
        style={{
          ...shellStyle,
          padding: "8px 12px",
          cursor: "pointer",
          border: "none",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 11 }}>Genius CMS</span>
        <span style={{ color: jsonColor, fontWeight: 600 }}>{jsonLabel}</span>
        <span style={{ opacity: 0.7 }}>▲</span>
      </button>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        ...shellStyle,
        padding: "10px 12px",
        maxWidth: 420,
        maxHeight: "50vh",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 12 }}>Genius CMS source</div>
        <button
          type="button"
          onClick={toggleMinimized}
          aria-label="Minimize Genius CMS debug panel"
          style={toggleButtonStyle}
        >
          − Minimize
        </button>
      </div>

      <div>Env: {getCurrentCmsEnvironment()}</div>
      <div>
        JSON: <span style={{ color: jsonColor, fontWeight: 600 }}>{jsonLabel}</span>
      </div>
      {meta.remoteUrl ? (
        <div style={{ opacity: 0.9, wordBreak: "break-all", marginTop: 2 }}>{meta.remoteUrl}</div>
      ) : null}
      {meta.fallbackReason ? (
        <div style={{ color: "#fbbf24", marginTop: 4 }}>{meta.fallbackReason}</div>
      ) : null}

      <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.15)" }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Marketing images</div>
        {shouldUseBundledCmsAssets() ? (
          <div style={{ color: sourceColor.local, fontWeight: 600 }}>
            Bundled content/assets/ (dev local mode)
          </div>
        ) : gcsBase ? (
          <div style={{ wordBreak: "break-all", opacity: 0.9 }}>
            Bucket: {gcsBase}/assets/...
          </div>
        ) : null}
        <div style={{ marginTop: 4 }}>
          <span style={{ color: sourceColor.gcs }}>{stats.gcsUrl} GCS URL</span>
          {" · "}
          <span style={{ color: sourceColor.local }}>{stats.localBundled} local</span>
          {" · "}
          <span style={{ color: sourceColor.project }}>{stats.projectApp} app icons</span>
        </div>
        {stats.gcsFailedUsedLocal > 0 ? (
          <div style={{ color: "#fbbf24", marginTop: 4 }}>
            {stats.gcsFailedUsedLocal} failed on GCS → used local fallback
          </div>
        ) : null}
      </div>

      {recent.length > 0 ? (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.15)" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Recent assets</div>
          {recent.map((entry) => (
            <div key={`${entry.at}-${entry.path}`} style={{ marginBottom: 6 }}>
              <span style={{ color: sourceColor[entry.source], fontWeight: 600 }}>
                [{entry.source}]
              </span>{" "}
              <span style={{ wordBreak: "break-all" }}>{entry.path}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ opacity: 0.65, marginTop: 6 }}>
        Filter Network tab by &quot;inmobius-assets&quot; to verify image requests
      </div>
    </div>
  );
};
